import React, { useState } from 'react';
import {
  Camera,
  CheckCircle,
  XCircle,
  Play,
  Monitor,
  Smartphone,
  Tablet,
  Mail,
  Send,
  Globe,
  Server,
  Database,
  FileText,
  ArrowRight,
  ArrowDown,
  Zap,
  Clock,
  GitBranch,
  Package,
  Terminal
} from 'lucide-react';

interface FlowStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  status: 'pending' | 'running' | 'success' | 'error';
  duration?: string;
  output?: string[];
}

export const PuppeteerTestFlow: React.FC = () => {
  const [activeFlow, setActiveFlow] = useState<'test' | 'deploy' | 'screenshot' | 'email'>('test');
  const [runningStep, setRunningStep] = useState<string | null>(null);

  const testFlowSteps: FlowStep[] = [
    {
      id: 'setup',
      title: 'Browser Setup',
      description: 'Launch Puppeteer browser instance',
      icon: <Monitor className="w-5 h-5" />,
      status: 'success',
      duration: '1.2s',
      output: ['Browser launched', 'Viewport: 1920x1080', 'Headless mode: true']
    },
    {
      id: 'navigate',
      title: 'Navigate to Pages',
      description: 'Visit each route in the application',
      icon: <Globe className="w-5 h-5" />,
      status: 'success',
      duration: '8.5s',
      output: ['/', '/markets', '/portfolio', '/email-test', '/philosophy']
    },
    {
      id: 'interact',
      title: 'UI Interactions',
      description: 'Click buttons, fill forms, test navigation',
      icon: <Play className="w-5 h-5" />,
      status: 'running',
      duration: '15.3s',
      output: ['Clicked "Create Market"', 'Filled email form', 'Submitted bet']
    },
    {
      id: 'screenshot',
      title: 'Capture Screenshots',
      description: 'Take screenshots of each page state',
      icon: <Camera className="w-5 h-5" />,
      status: 'pending',
      duration: '3.1s',
      output: ['12 screenshots captured', 'Saved to /tests/screenshots']
    },
    {
      id: 'validate',
      title: 'Validate Results',
      description: 'Check for errors and expected elements',
      icon: <CheckCircle className="w-5 h-5" />,
      status: 'pending',
      output: ['All assertions passed', 'No console errors detected']
    }
  ];

  const deployFlowSteps: FlowStep[] = [
    {
      id: 'build',
      title: 'Build Application',
      description: 'Vite production build',
      icon: <Package className="w-5 h-5" />,
      status: 'success',
      duration: '1.4s',
      output: ['601KB JS', '67KB CSS', 'Build optimized']
    },
    {
      id: 'pages',
      title: 'Deploy to Pages',
      description: 'CloudFlare Pages deployment',
      icon: <Globe className="w-5 h-5" />,
      status: 'success',
      duration: '2.8s',
      output: ['URL: futr-bet.pages.dev', '3 files uploaded']
    },
    {
      id: 'worker',
      title: 'Deploy Worker API',
      description: 'CloudFlare Worker deployment',
      icon: <Server className="w-5 h-5" />,
      status: 'success',
      duration: '3.5s',
      output: ['API: futr-bet-api.workers.dev', 'D1 Database connected']
    },
    {
      id: 'verify',
      title: 'Verify Deployment',
      description: 'Test production endpoints',
      icon: <CheckCircle className="w-5 h-5" />,
      status: 'pending',
      output: ['Health check passed', 'API responding']
    }
  ];

  const screenshotFlowSteps: FlowStep[] = [
    {
      id: 'desktop',
      title: 'Desktop Screenshots',
      description: '1920x1080 viewport',
      icon: <Monitor className="w-5 h-5" />,
      status: 'success',
      output: ['Homepage', 'Markets', 'Portfolio', 'Auth pages']
    },
    {
      id: 'tablet',
      title: 'Tablet Screenshots',
      description: '768x1024 viewport',
      icon: <Tablet className="w-5 h-5" />,
      status: 'success',
      output: ['Responsive layouts captured']
    },
    {
      id: 'mobile',
      title: 'Mobile Screenshots',
      description: '375x812 viewport',
      icon: <Smartphone className="w-5 h-5" />,
      status: 'success',
      output: ['Mobile navigation verified']
    },
    {
      id: 'save',
      title: 'Save & Organize',
      description: 'Store with timestamps',
      icon: <FileText className="w-5 h-5" />,
      status: 'success',
      output: ['12 screenshots saved', 'Timestamped: 2025-09-25']
    }
  ];

  const emailFlowSteps: FlowStep[] = [
    {
      id: 'send',
      title: 'Send Test Email',
      description: 'Via Resend API',
      icon: <Send className="w-5 h-5" />,
      status: 'success',
      duration: '0.8s',
      output: ['To: claude@futr.bet', 'Code: ABC123']
    },
    {
      id: 'webhook',
      title: 'Receive Webhook',
      description: 'Email forwarding service',
      icon: <Mail className="w-5 h-5" />,
      status: 'success',
      duration: '2.1s',
      output: ['From: test@example.com', 'Subject: Test Feedback']
    },
    {
      id: 'store',
      title: 'Store in Database',
      description: 'CloudFlare D1',
      icon: <Database className="w-5 h-5" />,
      status: 'success',
      duration: '0.3s',
      output: ['Stored in email_feedback table', 'ID: 8bb2a234...']
    },
    {
      id: 'poll',
      title: 'Poll for Updates',
      description: 'Real-time monitoring',
      icon: <Clock className="w-5 h-5" />,
      status: 'running',
      output: ['Polling every 5 seconds', '4 emails received']
    }
  ];

  const flows = {
    test: testFlowSteps,
    deploy: deployFlowSteps,
    screenshot: screenshotFlowSteps,
    email: emailFlowSteps
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-400 bg-green-950 border-green-600';
      case 'running': return 'text-blue-400 bg-blue-950 border-blue-600 animate-pulse';
      case 'error': return 'text-red-400 bg-red-950 border-red-600';
      default: return 'text-gray-400 bg-gray-800 border-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle className="w-4 h-4" />;
      case 'running': return <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />;
      case 'error': return <XCircle className="w-4 h-4" />;
      default: return <div className="w-4 h-4 border-2 border-gray-300 rounded-full" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-4 text-white">
            Puppeteer Test & Automation Flow
          </h1>
          <p className="text-gray-400">
            Automated testing, screenshot capture, and deployment pipeline
          </p>
        </div>

        {/* Flow Selector */}
        <div className="flex justify-center gap-4 mb-8">
          {[
            { id: 'test', label: 'Test Flow', icon: <Terminal className="w-4 h-4" /> },
            { id: 'deploy', label: 'Deploy Flow', icon: <GitBranch className="w-4 h-4" /> },
            { id: 'screenshot', label: 'Screenshots', icon: <Camera className="w-4 h-4" /> },
            { id: 'email', label: 'Email Flow', icon: <Mail className="w-4 h-4" /> }
          ].map(flow => (
            <button
              key={flow.id}
              onClick={() => setActiveFlow(flow.id as any)}
              className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all ${
                activeFlow === flow.id
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {flow.icon}
              {flow.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Flow Visualization */}
      <div className="max-w-6xl mx-auto">
        <div className="bg-gray-900 bg-opacity-90 rounded-2xl p-8 shadow-2xl border border-gray-700">
          {/* Command Display */}
          <div className="mb-6 p-4 bg-black rounded-lg font-mono text-sm border border-gray-700">
            <span className="text-green-500">$</span> npm run{' '}
            {activeFlow === 'test' && 'test:full'}
            {activeFlow === 'deploy' && 'deploy:all'}
            {activeFlow === 'screenshot' && 'screenshot:all'}
            {activeFlow === 'email' && 'test:email'}
          </div>

          {/* Flow Steps */}
          <div className="space-y-4">
            {flows[activeFlow].map((step, index) => (
              <div key={step.id} className="relative">
                {/* Connection Line */}
                {index < flows[activeFlow].length - 1 && (
                  <div className="absolute left-6 top-16 w-0.5 h-12 bg-gray-700" />
                )}

                {/* Step Card */}
                <div className={`border rounded-xl p-6 transition-all ${getStatusColor(step.status)}`}>
                  <div className="flex items-start gap-4">
                    {/* Status Icon */}
                    <div className="flex-shrink-0 mt-1">
                      {getStatusIcon(step.status)}
                    </div>

                    {/* Step Content */}
                    <div className="flex-grow">
                      <div className="flex items-center gap-3 mb-2">
                        {step.icon}
                        <h3 className="text-lg font-semibold">{step.title}</h3>
                        {step.duration && (
                          <span className="text-xs px-2 py-1 bg-gray-700 text-gray-300 rounded-full">
                            {step.duration}
                          </span>
                        )}
                      </div>

                      <p className="text-sm opacity-80 mb-3">{step.description}</p>

                      {step.output && (
                        <div className="bg-gray-900 rounded-lg p-3 font-mono text-xs">
                          {step.output.map((line, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <ArrowRight className="w-3 h-3 text-green-400" />
                              <span className="text-gray-300">{line}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Action Icon */}
                    {step.status === 'running' && (
                      <Zap className="w-5 h-5 text-yellow-400 animate-pulse" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary Stats */}
          <div className="mt-8 grid grid-cols-4 gap-4">
            <div className="bg-black rounded-lg p-4 text-center border border-gray-700">
              <div className="text-2xl font-bold text-green-400">12</div>
              <div className="text-xs text-gray-400">Pages Tested</div>
            </div>
            <div className="bg-black rounded-lg p-4 text-center border border-gray-700">
              <div className="text-2xl font-bold text-blue-400">3</div>
              <div className="text-xs text-gray-400">Viewports</div>
            </div>
            <div className="bg-black rounded-lg p-4 text-center border border-gray-700">
              <div className="text-2xl font-bold text-purple-400">100%</div>
              <div className="text-xs text-gray-400">Coverage</div>
            </div>
            <div className="bg-black rounded-lg p-4 text-center border border-gray-700">
              <div className="text-2xl font-bold text-yellow-400">25s</div>
              <div className="text-xs text-gray-400">Total Time</div>
            </div>
          </div>
        </div>

        {/* Architecture Overview */}
        <div className="mt-8 bg-gray-900 bg-opacity-90 rounded-2xl p-8 border border-gray-700">
          <h2 className="text-2xl font-bold mb-6 text-white">System Architecture</h2>

          <div className="grid grid-cols-3 gap-6">
            {/* Frontend */}
            <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Monitor className="w-6 h-6 text-blue-300" />
                <h3 className="font-semibold">Frontend</h3>
              </div>
              <div className="space-y-2 text-sm text-gray-300">
                <div>React + TypeScript</div>
                <div>CloudFlare Pages</div>
                <div>Tailwind CSS</div>
              </div>
            </div>

            {/* Testing */}
            <div className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Terminal className="w-6 h-6 text-purple-300" />
                <h3 className="font-semibold">Testing</h3>
              </div>
              <div className="space-y-2 text-sm text-gray-300">
                <div>Puppeteer</div>
                <div>TypeScript Tests</div>
                <div>Automated Screenshots</div>
              </div>
            </div>

            {/* Backend */}
            <div className="bg-gradient-to-br from-green-900 to-green-800 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Server className="w-6 h-6 text-green-300" />
                <h3 className="font-semibold">Backend</h3>
              </div>
              <div className="space-y-2 text-sm text-gray-300">
                <div>CloudFlare Workers</div>
                <div>D1 Database</div>
                <div>Resend Email API</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};